import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';

const CATEGORIES = [
  { icon: 'local_pizza', label: 'בצקים', cat: 'בצקים' },
  { icon: 'dinner_dining', label: 'איטלקי', cat: 'איטלקי' },
  { icon: 'set_meal', label: 'דגים', cat: 'דגים' },
  { icon: 'icecream', label: 'קינוחים', cat: 'קינוחים' },
  { icon: 'skillet', label: 'בשר', cat: 'בשר' },
  { icon: 'soup_kitchen', label: 'בישולים', cat: 'בישולים' },
];

const HomeScreen = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [recipeCount, setRecipeCount] = useState(0);
  const [displayName, setDisplayName] = useState('');
  const [featuredRecipes, setFeaturedRecipes] = useState([]);
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);


  // Touch   swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // בתוך ה-useEffect  של HomeScreen
  useEffect(() => {
    const fetchAllData = async () => {
      if (!user) return;

      // 1. ספירת המתכונים האישיים שלי
      const myRecipesQuery = query(collection(db, 'recipes'), where('userId', '==', user.uid));
      const mySnap = await getDocs(myRecipesQuery);
      setRecipeCount(mySnap.size);

      // 2. הבאת מומלצים מכלל המשתמשים (הקהילה)
      // אנחנו מביאים את כל המתכונים עם 5 כוכבים ותמונה
      const communityQ = query(
        collection(db, 'recipes'),
        where('rating', '==', 5)
      );

      const communitySnap = await getDocs(communityQ);
      const communityRecipes = communitySnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.imageUrl && r.userId !== user.uid) // רק עם תמונה ולא שלי
        .sort(() => 0.5 - Math.random()) // ערבוב אקראי כדי שכל פעם יראו משהו חדש
        .slice(0, 10); // ניקח את ה-10 הראשונים

      setFeaturedRecipes(communityRecipes);

      // שם המשתמש
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists() && userSnap.data().displayName) {
        setDisplayName(userSnap.data().displayName);
      } else {
        setDisplayName(user.displayName ? user.displayName.split(' ')[0] : 'שף');
      }
    };
    fetchAllData();
  }, [user]);

  const handleTouchStart = (e) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); };
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const d = touchStart - touchEnd;
    if (d > 50 && currentFeaturedIndex < featuredRecipes.length - 1) setCurrentFeaturedIndex(p => p + 1);
    else if (d < -50 && currentFeaturedIndex > 0) setCurrentFeaturedIndex(p => p - 1);
  };

  const currentRecipe = featuredRecipes[currentFeaturedIndex];

  return (
    <div className="bg-surface text-on-surface min-h-screen pb-32 font-body" dir="rtl">

      {/* ── TopAppBar ── */}
      <header className="w-full sticky top-0 z-50 bg-[#f9f9f9]/80 backdrop-blur-md">
        <div className="flex justify-between items-center px-6 py-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-headline font-extrabold tracking-tight text-primary">CookWithAI</span>
          </div>
          <div
            onClick={() => navigate('/profile')}
            className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-primary/20 cursor-pointer bg-surface-container-highest"
          >
            <img
              alt="פרופיל"
              className="w-full h-full object-cover"
              src={user?.photoURL || `https://ui-avatars.com/api/?name=${displayName}&background=006a62&color=fff`}
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pt-8">

        {/* ── Hero Greeting ── */}
        <section className="mb-10">
          <h1 className="text-4xl md:text-5xl font-headline font-extrabold tracking-tight mb-2">
            היי {displayName} 👋
          </h1>
          <p className="text-xl text-on-surface-variant font-medium">מה נבשל היום?</p>
        </section>

        {/* ── Bento Grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">

          {/* Extraction CTA */}
          <div className="md:col-span-2 relative group overflow-hidden rounded-xl signature-gradient p-8 flex flex-col justify-between min-h-[300px] shadow-[0px_20px_40px_rgba(0,106,98,0.18)]">
            <div className="relative z-10">
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/20 text-white text-sm font-semibold mb-4 backdrop-blur-sm">
                חדש: בינה מלאכותית ✨
              </span>
              <h2 className="text-3xl md:text-4xl font-headline font-bold text-white leading-tight mb-3 max-w-md">
                מסרטון למתכון בשניות ✨
              </h2>
              <p className="text-white/80 text-base max-w-sm">פשוט הדבק את הקישור והקסם יקרה בעצמו</p>
            </div>
            <div className="relative z-10 mt-6">
              <button
                onClick={() => navigate('/add')}
                className="home-extract-cta w-fit px-8 py-3 rounded-full font-bold text-white text-sm active:scale-95 transition-all shadow-md"
                style={{ background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.3)' }}
              >
                חילוץ מתכון ←
              </button>
            </div>
            <div className="absolute top-8 left-8 opacity-10 transform -rotate-12 group-hover:scale-110 duration-700 pointer-events-none">
              <span className="material-symbols-outlined text-[160px] text-white">auto_awesome</span>
            </div>
          </div>

          {/* Stats Card */}
          <div
            onClick={() => navigate('/book')}
            className="bg-surface-container-lowest rounded-xl p-8 flex flex-col justify-center items-center text-center border border-outline-variant/15 group hover:bg-primary-fixed transition-all duration-500 cursor-pointer recipe-card-shadow"
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-white/50 transition-colors">
              <span className="material-symbols-outlined text-primary text-3xl">menu_book</span>
            </div>
            <div className="text-5xl font-headline font-black text-on-surface mb-2">{recipeCount}</div>
            <div className="text-on-surface-variant font-semibold text-base">מתכונים בספר שלך</div>
            <button className="mt-6 text-primary font-bold flex items-center gap-2 group-hover:text-on-primary-fixed-variant transition-colors text-sm">
              לצפייה בכולם <span className="material-symbols-outlined text-sm">arrow_back</span>
            </button>
          </div>
        </div>

        {/* ── Featured Section ── */}
        <section className="mb-16">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl font-headline font-bold mb-2">מומלצי הקהילה ✨</h2>
              <div className="h-1.5 w-12 bg-tertiary rounded-full"></div>
            </div>
            {/* <button onClick={() => navigate('/favorites')} className="text-primary font-bold text-base hover:underline underline-offset-4">
              גלו עוד
            </button> */}
          </div>

          {featuredRecipes.length > 0 ? (
            <div
              className="relative flex flex-col md:flex-row items-center gap-0 cursor-pointer"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={() => navigate('/detail', { state: { recipe: currentRecipe } })}
            >
              <div className="w-full md:w-3/5 aspect-[4/3] rounded-xl overflow-hidden shadow-xl z-0">
                <img
                  alt={currentRecipe?.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  src={currentRecipe?.imageUrl}
                />
              </div>
              <div className="w-[90%] md:w-2/5 -mt-20 md:mt-0 md:-mr-24 glass-card p-8 rounded-xl border border-white/40 shadow-[0px_20px_40px_rgba(0,0,0,0.05)] z-10">
                <div className="flex gap-2 mb-4">
                  <span className="px-3 py-1 bg-tertiary-fixed text-on-tertiary-fixed text-xs font-bold rounded-full uppercase tracking-wider">
                    {currentRecipe?.category}
                  </span>
                  <span className="px-3 py-1 bg-primary-fixed text-on-primary-fixed text-xs font-bold rounded-full uppercase tracking-wider">
                    ★ {currentRecipe?.rating || 0}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-headline font-bold leading-tight mb-4">
                  {currentRecipe?.content?.split('\n')[0]?.substring(0, 45)}...
                </h3>
                <p className="text-on-surface-variant leading-relaxed mb-4 text-sm">לחץ לצפייה במתכון המלא!</p>
                <div className="flex gap-2">
                  {featuredRecipes.slice(0, 5).map((_, idx) => (
                    <div key={idx} className={`h-1.5 rounded-full transition-all ${idx === currentFeaturedIndex ? 'w-6 bg-primary' : 'w-2 bg-outline-variant'}`} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-surface-container-low rounded-xl p-12 text-center border-2 border-dashed border-primary/20">
              <span className="material-symbols-outlined text-5xl text-primary/40 mb-4 block">restaurant</span>
              <p className="text-on-surface-variant font-medium">
                כאן יופיעו המנות המנצחות של הקהילה! רק מתכונים שמשתמשים אחרים דירגו ב-5 כוכבים והוסיפו להם תמונות יזכו להופיע כאן.              </p>
            </div>
          )}
        </section>

        {/* ── Category Grid ── */}
        <section className="mb-12">
          <h2 className="text-2xl font-headline font-bold mb-6">חיפוש לפי קטגוריה</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {CATEGORIES.map((item, idx) => (
              <div
                key={idx}
                onClick={() => navigate('/book', { state: { category: item.cat } })}
                className="bg-surface-container-lowest p-5 rounded-xl text-center hover:bg-primary/5 transition-all cursor-pointer group recipe-card-shadow"
              >
                <span className="material-symbols-outlined text-3xl text-primary mb-2 block group-hover:scale-110 transition-transform">
                  {item.icon}
                </span>
                <span className="font-label font-semibold text-on-surface text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </section>

      </main>
    </div>
  );
};

export default HomeScreen;